import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { EditReimbursementModalButtons } from './EditReimbursementModalButtons';
import { PetCloudInput } from '../../../common/petCloud';
import { RadioButton } from '../../../common/RadioButton';

import {
  useSecondaryDirectDepositContent,
} from './hooks/useSecondaryDirectDepositContent';
import {
  ACCOUNT_TYPES,
  EDIT_ROUTING_NUMBER_LENGTH,
} from '../../../../constants';
import {
  changeSecondaryReimbursementAccountNumber,
  changeSecondaryReimbursementConfirmAccountNumber,
  changeSecondaryReimbursementRoutingNumber,
} from '../../../../actions';

import styles from '../css/SecondaryDirectDepositContent.module.css';

const SecondaryDirectDepositForm = ({
  accountNumberError,
  confirmAccountNumberError,
  handleAccountNumberChange = () => { },
  handleAccountTypeChange = () => { },
  handleConfirmAccountNumberChange = () => { },
  handleRoutingNumberChange = () => { },
  routingNumberError,
}) => {
  const { t } = useTranslation('myAccount');
  const secondaryMethod =
    useSelector(({ billingPayments }) => (
      billingPayments.reimbursementData.secondaryMethod
    ), shallowEqual);

  return (
    <form className={styles.formContainer}>
      <fieldset className={styles.radioButtonsContainer}>
        <legend>{t('reimbursementSection.editModal.accountType')}</legend>

        <div className={styles.radioButtonsRow}>
          <RadioButton
            blue
            checked={secondaryMethod.AccountType === ACCOUNT_TYPES.CHECKING}
            id="radio-checking"
            name="accountType"
            onChange={handleAccountTypeChange(ACCOUNT_TYPES.CHECKING)}
          >
            {t('reimbursementSection.editModal.checking')}
          </RadioButton>

          <RadioButton
            blue
            checked={secondaryMethod.AccountType === ACCOUNT_TYPES.SAVINGS}
            id="radio-savings"
            name="accountType"
            onChange={handleAccountTypeChange(ACCOUNT_TYPES.SAVINGS)}
          >
            {t('reimbursementSection.editModal.savings')}
          </RadioButton>
        </div>
      </fieldset>

      <div className={styles.inputMargin}>
        <PetCloudInput
          ariaProps={{
            'aria-label': t('reimbursementSection.editModal.routingNumber'),
          }}
          error={routingNumberError}
          id="bankRoutingNumber"
          maxLength={EDIT_ROUTING_NUMBER_LENGTH}
          onChange={handleRoutingNumberChange}
          pattern="[0-9]*"
          title={t('reimbursementSection.editModal.routingNumber')}
          type="number"
          value={secondaryMethod.RoutingNumber}
        />
      </div>

      <div className={styles.accountNumberContainer}>
        <PetCloudInput
          ariaProps={{
            'aria-label': t('reimbursementSection.editModal.accountNumber'),
          }}
          error={accountNumberError}
          id="bankAccountNumber"
          onChange={handleAccountNumberChange}
          pattern="[0-9]*"
          title={t('reimbursementSection.editModal.accountNumber')}
          type="number"
          value={secondaryMethod.AccountNumber}
        />

        <PetCloudInput
          error={confirmAccountNumberError}
          id="confirmBankAccountNumber"
          onChange={handleConfirmAccountNumberChange}
          pattern="[0-9]*"
          title={t('reimbursementSection.editModal.confirmAccountNumber')}
          type="number"
          value={secondaryMethod.ConfirmAccountNumber}
        />
      </div>
    </form>
  );
};

const SecondaryDirectDepositContent = ({
  goNext = () => { },
  onCancel = () => { },
}) => {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();

  const {
    handleExistingCheckbox,
    secondaryMethod,
    ...secondaryDirectDepositHandlers
  } = useSecondaryDirectDepositContent({ onCancel });

  const handleCancel = useCallback(() => {
    dispatch(changeSecondaryReimbursementAccountNumber(''));
    dispatch(changeSecondaryReimbursementConfirmAccountNumber(''));
    dispatch(changeSecondaryReimbursementRoutingNumber(''));
    handleExistingCheckbox(false);
    onCancel();
  }, [dispatch, onCancel]);

  return (
    <div className={styles.container}>
      <h4 className={styles.contentTitle}>
        {t('reimbursementSection.editModal.careCredit')}
      </h4>

      <SecondaryDirectDepositForm
        {...secondaryDirectDepositHandlers}
      />

      <EditReimbursementModalButtons
        finalStep
        handleEditReimbursementAccept={goNext}
        handleEditReimbursementCancel={handleCancel}
        isFormInvalid={!secondaryMethod.isExisting
          && secondaryDirectDepositHandlers.isBankFormInvalid}
      />
    </div>
  );
};

export { SecondaryDirectDepositContent };
